import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Add,
  Delete,
  Edit,
  Save,
} from '@mui/icons-material';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import Swal from 'sweetalert2';

import {
  OS_ANDROID,
  OS_IOS,
  OS_LABELS,
  OS_WEB_APP,
  createAttributionOption,
  deleteAttributionOption,
  fetchAttributionOptions,
  updateAttributionOption,
} from '../../../actions/attributionOptionsActions';
import {
  fetchGeneralInfo,
  updateGeneralInfo,
} from '../../../actions/generalInfoActions';
import LoadingBackdrop from '../../Common/LoadingBackdrop';
import MainContentContainer from '../../MainContentContainer/MainContentContainer';

// Legacy single-value key — read as a fallback for any platform whose
// per-platform key hasn't been set yet. Product can (and should) set each
// platform individually going forward; see APIv1 UserAttribution::shouldShow…
// for the matching resolution logic.
const LEGACY_ROLLOUT_KEY = 'user_attribution_percentage';

// Per-platform rollout keys. Must match APIv1's
// UserAttribution::PERCENTAGE_KEY_BY_OS.
const ROLLOUT_KEYS = {
  [OS_IOS]: 'user_attribution_percentage_ios',
  [OS_ANDROID]: 'user_attribution_percentage_android',
  [OS_WEB_APP]: 'user_attribution_percentage_webapp',
};

const PLATFORM_ORDER = [OS_IOS, OS_ANDROID, OS_WEB_APP];

function OptionsTable({ title, os, options, onEdit, onDelete }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <TableContainer>
        <Table size="small" aria-label={`${title} attribution options`}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ width: 80 }}><strong>ID</strong></TableCell>
              <TableCell><strong>Value</strong></TableCell>
              <TableCell align="right" sx={{ width: 120 }}><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {options.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    No options yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              options.map((option) => (
                <TableRow key={option.id}>
                  <TableCell>{option.id}</TableCell>
                  <TableCell>{option.value}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit" placement="top">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => onEdit(option)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" placement="top">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDelete(option)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function validatePercentage(raw) {
  if (raw === '' || raw === null || raw === undefined) {
    return 'Required';
  }
  if (!/^\d+$/.test(String(raw))) {
    return 'Integer 0 - 100';
  }
  const n = parseInt(raw, 10);
  if (n < 0 || n > 100) {
    return '0 - 100';
  }
  return '';
}

export default function AttributionOptionsPage() {
  const dispatch = useDispatch();

  const attributionState = useSelector((state) => state.attribution_options);
  const generalInfo = useSelector((state) => state.general_info?.general_info || []);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create; otherwise the option row
  const [dialogValue, setDialogValue] = useState('');
  const [dialogOs, setDialogOs] = useState(OS_IOS);
  const [saving, setSaving] = useState(false);

  // One input per platform. Initial values populated from the general_info
  // store in the useEffect below.
  const [rolloutInputs, setRolloutInputs] = useState({
    [OS_IOS]: '',
    [OS_ANDROID]: '',
    [OS_WEB_APP]: '',
  });
  const [rolloutErrors, setRolloutErrors] = useState({
    [OS_IOS]: '',
    [OS_ANDROID]: '',
    [OS_WEB_APP]: '',
  });
  const [savingRollout, setSavingRollout] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    dispatch(fetchAttributionOptions());
    dispatch(fetchGeneralInfo());
  }, [dispatch]);

  // Sync each input from the general_info store. For any platform whose
  // per-platform key isn't set yet, fall back to the legacy single-value key
  // so the admin sees a sensible starting point. When they hit Save, the
  // per-platform keys get written (legacy is untouched) and subsequent loads
  // read the per-platform values directly.
  useEffect(() => {
    // Skip sync while a save is in flight: the generalInfo reducer's
    // UPDATE_GENERAL_INFO returns a new array reference even when the key
    // didn't exist (first-save case for per-platform keys), which would
    // otherwise fire this effect mid-save, see the per-platform keys still
    // missing, fall back to the legacy value, and stomp on what the user
    // just typed. The save flow awaits the follow-up fetchGeneralInfo
    // before flipping savingRollout back to false, so by the time this
    // effect runs post-save, the store has the freshly-written keys.
    if (savingRollout) return;

    const lookup = (key) => generalInfo.find((row) => row.general_info_key === key);
    const legacy = lookup(LEGACY_ROLLOUT_KEY);
    const legacyValue = legacy?.general_info_value;

    const nextInputs = { ...rolloutInputs };
    let changed = false;

    PLATFORM_ORDER.forEach((os) => {
      const row = lookup(ROLLOUT_KEYS[os]);
      let value;
      if (row && row.general_info_value !== undefined && row.general_info_value !== null) {
        value = String(row.general_info_value);
      } else if (legacyValue !== undefined && legacyValue !== null) {
        value = String(legacyValue);
      } else {
        value = '';
      }
      if (nextInputs[os] !== value) {
        nextInputs[os] = value;
        changed = true;
      }
    });

    if (changed) {
      setRolloutInputs(nextInputs);
      setRolloutErrors({ [OS_IOS]: '', [OS_ANDROID]: '', [OS_WEB_APP]: '' });
    }
  }, [generalInfo, savingRollout]);

  const iosOptions = useMemo(
    () => [...(attributionState?.iOS || [])].sort((a, b) => a.id - b.id),
    [attributionState?.iOS]
  );
  const androidOptions = useMemo(
    () => [...(attributionState?.android || [])].sort((a, b) => a.id - b.id),
    [attributionState?.android]
  );
  const webAppOptions = useMemo(
    () => [...(attributionState?.webApp || [])].sort((a, b) => a.id - b.id),
    [attributionState?.webApp]
  );

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const openCreateDialog = () => {
    setEditing(null);
    setDialogValue('');
    setDialogOs(OS_IOS);
    setDialogOpen(true);
  };

  const openEditDialog = (option) => {
    setEditing(option);
    setDialogValue(option.value || '');
    setDialogOs(option.os);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const handleDialogSave = async () => {
    const trimmed = dialogValue.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      if (editing) {
        await dispatch(updateAttributionOption(editing.id, { value: trimmed, os: dialogOs }));
      } else {
        await dispatch(createAttributionOption({ value: trimmed, os: dialogOs }));
      }
      // Re-fetch to stay in sync with server-side grouping.
      await dispatch(fetchAttributionOptions());
      setDialogOpen(false);
    } catch (error) {
      // errors are surfaced via Swal in the action creator
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (option) => {
    const result = await Swal.fire({
      title: 'Delete attribution option?',
      text: `"${option.value}" (${OS_LABELS[option.os] || 'OS'}) will be removed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      await dispatch(deleteAttributionOption(option.id, option.os));
      await dispatch(fetchAttributionOptions());
    } catch (error) {
      // handled via Swal
    }
  };

  const handleRolloutChange = (os) => (e) => {
    const v = e.target.value;
    setRolloutInputs((prev) => ({ ...prev, [os]: v }));
    setRolloutErrors((prev) => ({ ...prev, [os]: validatePercentage(v) }));
  };

  const hasAnyRolloutError = PLATFORM_ORDER.some((os) => !!rolloutErrors[os]);

  const handleRolloutSave = async () => {
    // Validate each before firing any requests.
    const errors = {};
    PLATFORM_ORDER.forEach((os) => {
      errors[os] = validatePercentage(rolloutInputs[os]);
    });
    if (Object.values(errors).some(Boolean)) {
      setRolloutErrors(errors);
      return;
    }

    setSavingRollout(true);
    try {
      // Save each platform-specific key. We intentionally don't write the
      // legacy key anymore — APIv1 falls back to it only when the
      // platform-specific key is missing entirely, so once all three are
      // written the legacy value is effectively unused.
      for (const os of PLATFORM_ORDER) {
        // eslint-disable-next-line no-await-in-loop
        await dispatch(updateGeneralInfo({
          general_info_key: ROLLOUT_KEYS[os],
          general_info_value: String(parseInt(rolloutInputs[os], 10)),
        }));
      }
      showSnackbar('Rollout percentages saved', 'success');
      // Await the refetch so the store has the freshly-written per-platform
      // keys before we flip savingRollout. The sync effect is guarded on
      // savingRollout; without the await it would fire between flipping the
      // flag and the fetch resolving, and briefly reset the inputs.
      await dispatch(fetchGeneralInfo());
    } catch (error) {
      // Error UX is handled inside updateGeneralInfo via the shared errorHandler (Swal),
      // same as the attribution CRUD handlers on this page. Don't show a second snackbar.
    } finally {
      setSavingRollout(false);
    }
  };

  const loading = !!attributionState?.loading;

  return (
    <MainContentContainer>
      <LoadingBackdrop open={loading} />
      <AppBar position="sticky">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Attribution Options
          </Typography>
          <Button
            color="inherit"
            startIcon={<Add />}
            onClick={openCreateDialog}
          >
            Add Option
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ mt: 3, mb: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Rollout percentage
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls what percentage of eligible users see the onboarding
            attribution screen on each platform. Each value is stored as its
            own <code>user_attribution_percentage_*</code> key in general info.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {PLATFORM_ORDER.map((os) => (
              <TextField
                key={os}
                label={OS_LABELS[os]}
                value={rolloutInputs[os]}
                onChange={handleRolloutChange(os)}
                error={!!rolloutErrors[os]}
                helperText={rolloutErrors[os] || 'Integer 0 - 100'}
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  inputProps: { inputMode: 'numeric', pattern: '[0-9]*' },
                }}
                sx={{ width: 180 }}
              />
            ))}
            <Button
              variant="contained"
              color="success"
              startIcon={<Save />}
              onClick={handleRolloutSave}
              disabled={hasAnyRolloutError || savingRollout}
              sx={{ alignSelf: 'flex-start', mt: '4px' }}
            >
              Save
            </Button>
          </Box>
        </Paper>
      </Box>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <OptionsTable
            title="iOS"
            os={OS_IOS}
            options={iosOptions}
            onEdit={openEditDialog}
            onDelete={handleDelete}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <OptionsTable
            title="Android"
            os={OS_ANDROID}
            options={androidOptions}
            onEdit={openEditDialog}
            onDelete={handleDelete}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <OptionsTable
            title="Web App"
            os={OS_WEB_APP}
            options={webAppOptions}
            onEdit={openEditDialog}
            onDelete={handleDelete}
          />
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? 'Edit attribution option' : 'New attribution option'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Value"
              value={dialogValue}
              onChange={(e) => setDialogValue(e.target.value)}
              fullWidth
              autoFocus
              placeholder="e.g. Podcast"
            />
            <FormControl fullWidth>
              <InputLabel id="attribution-os-label">OS</InputLabel>
              <Select
                labelId="attribution-os-label"
                label="OS"
                value={dialogOs}
                onChange={(e) => setDialogOs(Number(e.target.value))}
              >
                <MenuItem value={OS_IOS}>iOS</MenuItem>
                <MenuItem value={OS_ANDROID}>Android</MenuItem>
                <MenuItem value={OS_WEB_APP}>Web App</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleDialogSave}
            variant="contained"
            disabled={saving || !dialogValue.trim()}
          >
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainContentContainer>
  );
}
